import type { CampaignPopShot } from 'c-sdk';
import type { AxiosProgressEvent } from 'axios';
import { PermissionName } from 'c-sdk';

/**
 * POP shot rules shared by the campaign POP images tab, the line item upload dialog and the
 * tiles. Kept free of React so the decisions can be tested on their own.
 */

export type PopShotType = CampaignPopShot['type'];

/** Upload a POP shot (API: `CampaignPopShotCreateRequest`). */
export const POP_SHOT_UPLOAD_PERMISSIONS: PermissionName[] = [
    PermissionName.UniledportalFileCreate,
    PermissionName.UniledportalCampaign_pop_shotsCreate,
];

/** Download the campaign's POP shots as a zip. */
export const POP_SHOT_DOWNLOAD_PERMISSIONS: PermissionName[] = [
    PermissionName.UniledportalFileRead,
    PermissionName.UniledportalCampaign_pop_shotsRead,
];

/** Shows the delete button on a POP shot tile. */
export const POP_SHOT_DELETE_PERMISSIONS: PermissionName[] = [
    PermissionName.UniledportalFileDestroy,
    PermissionName.UniledportalCampaign_pop_shotsRead,
];

/** Shows the "professional" toggle on a POP shot tile (API: `CampaignPopShotUpdateRequest`). */
export const POP_SHOT_UPDATE_PERMISSIONS: PermissionName[] = [
    PermissionName.UniledportalFileCreate,
    PermissionName.UniledportalCampaign_pop_shotsUpdate,
];

/** What the file picker offers. The API's mimetype list is the real gate. */
export const POP_SHOT_ACCEPT = 'image/*,video/*,application/pdf';

const disallowedFilenameChars = /[\\/:*?"<>|~]/;

export const isValidPopShotFilename = (filename: string) => !disallowedFilenameChars.test(filename);

export const invalidPopShotFilenamesMessage = (invalidNames: string[]): string | null =>
    invalidNames.length > 0
        ? `Some files were skipped due to invalid characters in their names: \n${invalidNames.join(
              '\n',
          )}    \n Invalid characters are: '\\', '/', ':', '*', '?', '"', '<', '>', '|', '~'`
        : null;

export const popShotType = (professional: boolean): PopShotType =>
    professional ? 'professional' : 'pop_shot';

/** The body of one POP shot upload (`POST campaign-pop-shot/create`). */
export const buildPopShotUploadFormData = (
    campaignId: number | string,
    file: File,
    lineItemId: string,
    professional: boolean,
) => {
    const formData = new FormData();
    formData.set('campaign_id', String(campaignId));
    formData.set('file', file);
    formData.set('line_item_id', lineItemId);
    formData.set('type', popShotType(professional));
    return formData;
};

/** The body of a bulk retype (`PUT campaign-pop-shot`). */
export const popShotRetypePayload = (
    popShots: Pick<CampaignPopShot, 'id'>[],
    professional: boolean,
): Pick<CampaignPopShot, 'id' | 'type'>[] =>
    popShots.map(pop => ({ id: pop.id, type: popShotType(professional) }));

/** Swaps in the updated copies the API answered with, keeping the rest. */
export const mergeUpdatedPopShots = (
    current: CampaignPopShot[],
    updated: CampaignPopShot[],
): CampaignPopShot[] => current.map(pop => updated.find(p => p.id === pop.id) ?? pop);

/**
 * The POP images gallery: which shots show, and whether to offer the "only professional" filter.
 * Shots without a file are listed by the API but never shown.
 */
export const popShotGallery = (
    popShots: CampaignPopShot[] | null | undefined,
    onlyPro: boolean,
) => {
    const listed = Array.isArray(popShots)
        ? popShots.filter(pop => (onlyPro ? pop.type === 'professional' : true))
        : [];

    return {
        listed,
        withFiles: listed.filter(pop => pop.file != null),
        hasProPopShots: listed.some(pop => pop.type === 'professional'),
    };
};

/** A tile shows its controls when the user may do either thing. */
export const canEditOrDeletePopShot = (hasAll: (perms: PermissionName[]) => boolean) =>
    hasAll(POP_SHOT_DELETE_PERMISSIONS) || hasAll(POP_SHOT_UPDATE_PERMISSIONS);

/** Nobody can upload and there is nothing to look at: show the "coming soon" placeholder. */
export const showPopImagesComingSoon = (canUpload: boolean, hasPopShots: boolean) =>
    !canUpload && !hasPopShots;

/** How many shots a line item row hides behind its "+N" button. */
export const popShotPreviewOverflow = (count: number | null | undefined, shown: number) =>
    (count ?? 0) > shown ? Math.abs((count ?? 0) - shown) : 0;

export const uploadProgressPercentage = (
    progress: Pick<AxiosProgressEvent, 'loaded' | 'total'> | null | undefined,
) =>
    progress?.total != null && progress?.loaded != null
        ? (progress.loaded / progress.total) * 100
        : 0;

/** Every queued file has either uploaded or failed. */
export const allPopShotUploadsSettled = (queued: number, uploaded: number, failed: number) =>
    queued === uploaded + failed;
